// Consumer-side mirror of the admin editor's custom-page config. Persisted as a single JSON
// string on IStorefrontSettings.pagesConfig. Custom pages are additional homepages served at
// /{slug} (e.g. /ai), each with its own banners + content/product sections.
//
// IMPORTANT: strictly additive and defensive — an absent/malformed blob yields no pages, so a
// storefront with no custom pages behaves exactly as it does today (only the main homepage).

import { IStorefrontSettings } from '../../Api/Models/StorefrontSettings';

// One custom page's metadata. Banners and sections themselves are NOT carried here — they arrive
// separately (banners on settings.storefrontBanners tagged with `page`; sections from the featured
// endpoint filtered by page). This is only the page's identity + presentation knobs.
export interface IStorefrontCustomPage {
    id: string;                 // page GUID; matches IStorefrontBanner.page and the featured `page` arg
    slug: string;              // URL segment the page is served at (/{slug}); compared case-insensitively
    title?: string;            // document.title for the page
    enabled?: boolean;         // only enabled pages resolve; ABSENT MEANS ENABLED (only an explicit false disables)
    sortOrder?: number;        // authoring order (not used for routing; reserved for admin listing)
    bannerHeight?: number;     // per-page banner height override (px); falls back to settings.bannerHeight
    // Per-page hero fields — mirror homePageConfig's bannerFullWidth/bannerImageFit/bannerUnderHeader/
    // bannerUnderHeaderBackground/bannerUnderHeaderShadow, applied to THIS page's banner. All strictly
    // opt-in: absent = today's page-width banner (no hero treatment). See HomePage's hero-source resolve.
    bannerFullWidth?: boolean;             // render this page's banner full-bleed (edge-to-edge); content stays at page maxWidth
    bannerImageFit?: 'fit' | 'fill';       // 'fit' letterboxes (object-fit: contain); 'fill' crops (object-fit: cover)
    bannerUnderHeader?: boolean;           // float the transparent nav header over the full-bleed hero (only meaningful with bannerFullWidth; desktop only)
    bannerUnderHeaderBackground?: string;  // CSS color tinting the floating header bar before scroll
    bannerUnderHeaderShadow?: string;      // CSS box-shadow under the floating header bar before scroll
}

export interface IPagesConfig {
    pages?: IStorefrontCustomPage[];
}

export const DEFAULT_PAGES_CONFIG: IPagesConfig = {};

// Safe parse: returns an empty config on absence or malformed JSON, so an unconfigured storefront
// never throws and never surfaces phantom pages.
export function parsePagesConfig(raw?: string): IPagesConfig {
    if (!raw) return { ...DEFAULT_PAGES_CONFIG };
    try {
        const parsed = JSON.parse(raw) as IPagesConfig;
        return { ...DEFAULT_PAGES_CONFIG, ...parsed };
    } catch {
        return { ...DEFAULT_PAGES_CONFIG };
    }
}

// Resolve a URL slug to an enabled custom page, or undefined when none matches. The slug compare is
// trimmed and case-insensitive so /AI and /ai reach the same page. Enabled defaults to TRUE (the
// contract with the admin editor): only an explicit enabled:false disables a page, and a
// linked-but-turned-off page 404s just like a nonexistent one.
export function findPageBySlug(
    settings: Pick<IStorefrontSettings, 'pagesConfig'> | undefined,
    slug: string | undefined,
): IStorefrontCustomPage | undefined {
    if (!settings || !slug) return undefined;
    const target = slug.trim().toLowerCase();
    if (!target) return undefined;
    const pages = parsePagesConfig(settings.pagesConfig).pages ?? [];
    return pages.find(p => p.enabled !== false && (p.slug ?? '').trim().toLowerCase() === target);
}
